import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { login, googleLogin, getPublicConfig, formatApiErrorDetail } from "../lib/api";
import { GrainGradient, grainGradientPresets } from "@paper-design/shaders-react";
import "../studio.css";

export function routeForUser(user) {
  if (!user) return "/login";
  if (user.role === "platform_admin") return "/admin";
  if (user.companyStatus !== "approved") return "/pending";
  return "/";
}

let gsiPromise = null;
function loadGsi() {
  if (window.google && window.google.accounts) return Promise.resolve();
  if (!gsiPromise) {
    gsiPromise = new Promise((resolve, reject) => {
      const el = document.createElement("script");
      el.src = "https://accounts.google.com/gsi/client"; el.async = true;
      el.onload = resolve; el.onerror = () => { gsiPromise = null; reject(new Error("Google script failed to load")); };
      document.head.appendChild(el);
    });
  }
  return gsiPromise;
}

// Google Identity Services: Google renders its own button; we receive a signed ID token and the
// backend verifies it. Hidden when the server has no GOOGLE_CLIENT_ID configured.
export function GoogleButton({ label = "Continue with Google" }) {
  const box = useRef(null);
  const nav = useNavigate();
  const [state, setState] = useState("loading"); // loading | ready | off
  const [err, setErr] = useState("");

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { googleClientId } = await getPublicConfig();
        if (!googleClientId) { if (!dead) setState("off"); return; }
        await loadGsi();
        if (dead || !box.current) return;
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (resp) => {
            setErr("");
            try {
              const { user } = await googleLogin(resp.credential);
              nav(routeForUser(user));
            } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Google sign-in failed"); }
          },
        });
        const text = /sign up/i.test(label) ? "signup_with" : /continue/i.test(label) ? "continue_with" : "signin_with";
        window.google.accounts.id.renderButton(box.current, { theme: "outline", size: "large", text, shape: "rectangular", width: box.current.offsetWidth || 320 });
        setState("ready");
      } catch (e) { if (!dead) setState("off"); }
    })();
    return () => { dead = true; };
  }, [label, nav]);

  if (state === "off") return null;
  return (
    <div data-testid="google-btn">
      <div ref={box} style={{ minHeight: 44, display: "flex", justifyContent: "center" }} />
      {err && <div className="sl-err" role="alert">{err}</div>}
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const location = useLocation();
  const [err, setErr] = useState(location.state?.error || "");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { user } = await login(email, password);
      nav(routeForUser(user));
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="sl-login">
      <div className="sl-left">
        <div className="grain-fill" data-testid="grain-gradient-bg">
          <GrainGradient
            {...grainGradientPresets[0]}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          />
        </div>
        <div className="sl-eyebrow">CLOUDTV</div>
        <div className="sl-hero">
          <div className="sl-wordmark">Studio</div>
          <div className="sl-tag">An open tool to build, serve and measure Interactive CTV creatives</div>
        </div>
      </div>

      <div className="sl-right">
        <form className="sl-form" onSubmit={submit} data-testid="login-form">
          <h1>Sign in to your account</h1>
          <p className="sl-sub">Enter your credentials to access the dashboard</p>

          <GoogleButton label="Sign in with Google" />

          <div className="sl-divider"><span>OR SIGN IN WITH CREDENTIALS</span></div>

          {err && <div className="sl-err" data-testid="login-error">{err}</div>}

          <div className="sl-field">
            <label>Work email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email" spellCheck={false} placeholder="you@company.com" autoComplete="email" />
          </div>

          <div className="sl-field">
            <label>Password</label>
            <div className="sl-pw">
              <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" placeholder="Enter your password" autoComplete="current-password" />
              <button type="button" className="sl-eye" onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"} data-testid="toggle-password">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button className="sl-signin" type="submit" disabled={loading} data-testid="login-submit">
            {loading ? "Signing in…" : "Sign In"} <ArrowRight size={18} />
          </button>

          <p className="sl-foot">New here? <Link to="/signup" data-testid="to-signup">Request access</Link></p>
        </form>
      </div>
    </div>
  );
}
